<template>
  <div class="vt-page py-3">
    <header class="vt-toolbar">
      <div class="vt-toolbar__title">
        <span class="vt-toolbar__accent" aria-hidden="true"></span>
        <div>
          <h1>Credentials Vault</h1>
          <p>Encrypted store for shared platform logins. Super-admin only — every reveal is logged.</p>
        </div>
      </div>
      <div class="vt-toolbar__actions">
        <button
          v-if="lockStatus === 'unlocked'"
          type="button"
          class="vt-btn vt-btn--primary"
          @click="goCreate"
        >+ New Entry</button>
        <button
          v-if="lockStatus === 'unlocked'"
          type="button"
          class="vt-btn vt-btn--ghost"
          :class="{ 'is-loading': isLoading }"
          :disabled="isLoading"
          @click="loadEntries"
        >{{ isLoading ? 'Refreshing…' : 'Refresh' }}</button>
        <button
          v-if="lockStatus === 'unlocked'"
          type="button"
          class="vt-btn vt-btn--ghost vt-btn--danger"
          @click="lockVault"
        >Lock</button>
      </div>
    </header>

    <CAlert v-if="errorMessage" color="danger" show class="vt-alert">{{ errorMessage }}</CAlert>

    <div v-if="lockStatus === 'unknown'" class="vt-lockscreen">
      <span class="vt-spinner" aria-hidden="true"></span>
      <p>Checking vault status…</p>
    </div>

    <div v-else-if="lockStatus === 'locked'" class="vt-lockscreen">
      <div
        class="vt-lockscreen__card"
        :class="{
          'vt-lockscreen__card--shake': cardShake,
          'vt-lockscreen__card--opening': unlockAnimating,
        }"
      >
        <span
          class="vt-lockscreen__dial"
          :class="`vt-lockscreen__dial--${dialState}`"
          aria-hidden="true"
        >
          <svg viewBox="0 0 60 60" width="56" height="56" fill="none">
            <!-- vault body -->
            <circle cx="30" cy="30" r="27" fill="rgba(0, 0, 0, 0.20)" />
            <circle cx="30" cy="30" r="26" stroke="rgba(255, 255, 255, 0.35)" stroke-width="1" />
            <!-- fixed cardinal ticks -->
            <g stroke="rgba(255, 255, 255, 0.55)" stroke-width="1.6" stroke-linecap="round">
              <line x1="30" y1="5"  x2="30" y2="10" />
              <line x1="55" y1="30" x2="50" y2="30" />
              <line x1="30" y1="55" x2="30" y2="50" />
              <line x1="5"  y1="30" x2="10" y2="30" />
            </g>
            <!-- rotating rotor -->
            <g class="vt-dial__rotor">
              <circle cx="30" cy="30" r="18" fill="rgba(255, 255, 255, 0.10)" stroke="rgba(255, 255, 255, 0.75)" stroke-width="1.4" />
              <!-- inner ticks on the rotor face -->
              <g stroke="rgba(255, 255, 255, 0.55)" stroke-width="1.1" stroke-linecap="round">
                <line x1="30" y1="14" x2="30" y2="17" />
                <line x1="44" y1="30" x2="41" y2="30" />
                <line x1="30" y1="46" x2="30" y2="43" />
                <line x1="16" y1="30" x2="19" y2="30" />
              </g>
              <!-- pointer -->
              <line x1="30" y1="30" x2="30" y2="15" stroke="#ffffff" stroke-width="2.6" stroke-linecap="round" />
              <!-- centre bolt -->
              <circle cx="30" cy="30" r="3.5" fill="#ffffff" />
            </g>
          </svg>
        </span>
        <h2>Enter vault pincode</h2>
        <p>This area requires a second factor on top of your superadmin role. The pincode is rotated by the system owner.</p>

        <div v-if="!pinConfigured" class="vt-lockscreen__alert">
          Vault pincode is not configured on the server. Set <code>VAULT_PIN</code> in the server <code>.env</code> file.
        </div>

        <form v-else class="vt-lockscreen__form" @submit.prevent="submitPin">
          <div class="vt-pin">
            <input
              v-for="i in 6"
              :key="i"
              ref="pinInputs"
              type="text"
              inputmode="numeric"
              autocomplete="one-time-code"
              maxlength="1"
              :class="['vt-pin__box', { 'vt-pin__box--filled': !!pinDigits[i - 1] }]"
              :value="pinDigits[i - 1]"
              :disabled="isUnlocking"
              @input="onPinInput(i - 1, $event)"
              @keydown="onPinKeydown(i - 1, $event)"
              @paste.prevent="onPinPaste"
              @focus="$event.target.select()"
            />
          </div>

          <div v-if="pinError" class="vt-lockscreen__error">{{ pinError }}</div>

          <button
            type="submit"
            class="vt-btn vt-btn--primary vt-lockscreen__submit"
            :disabled="isUnlocking || pinDigits.join('').length !== 6"
          >{{ isUnlocking ? 'Verifying…' : 'Unlock vault' }}</button>
        </form>
      </div>
    </div>

    <div v-else class="vt-layout">
      <aside class="vt-rail" :class="{ 'vt-rail--hidden-mobile': selectedId !== null }">
        <div class="vt-rail__head">
          <div class="vt-rail__search">
            <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <circle cx="11" cy="11" r="8"/>
              <line x1="21" y1="21" x2="16.65" y2="16.65"/>
            </svg>
            <input
              type="text"
              class="vt-rail__search-input"
              placeholder="Search entries"
              :value="searchTerm"
              @input="searchTerm = $event.target.value"
            />
          </div>
          <AppDropdown
            v-model="categoryFilter"
            :options="categoryOptions"
            placeholder="All categories"
          />
        </div>

        <div class="vt-rail__count">
          {{ filteredEntries.length }} {{ filteredEntries.length === 1 ? 'entry' : 'entries' }}
        </div>

        <div class="vt-rail__list">
          <div v-if="isLoading && !items.length" class="vt-rail__placeholder">Loading…</div>
          <div v-else-if="!filteredEntries.length" class="vt-rail__placeholder">
            {{ items.length ? 'No entries match your filter.' : 'No vault entries yet. Click + New Entry to create one.' }}
          </div>
          <button
            v-for="entry in filteredEntries"
            :key="entry.id"
            type="button"
            :class="['vt-rail__item', { 'is-active': entry.id === selectedId }]"
            @click="selectEntry(entry)"
          >
            <span
              class="vt-avatar"
              :class="{ 'vt-avatar--logo': logoFor(entry) }"
              :data-cat="entry.category"
              aria-hidden="true"
            >
              <img
                v-if="logoFor(entry)"
                :src="logoFor(entry).src"
                :alt="entry.name"
                @error="onLogoError(entry, logoFor(entry).source)"
              />
              <template v-else>{{ initialsFor(entry.name) }}</template>
            </span>
            <span class="vt-rail__item-text">
              <span class="vt-rail__item-name">
                {{ entry.name }}
                <span v-if="entry.pinned" class="vt-rail__pin" title="Pinned">★</span>
              </span>
              <span class="vt-rail__item-id">{{ entry.hint || categoryLabel(entry.category) }}</span>
            </span>
          </button>
        </div>
      </aside>

      <section class="vt-detail" :class="{ 'vt-detail--hidden-mobile': selectedId === null }">
        <div v-if="selectedId === null" class="vt-detail__empty">
          <svg viewBox="0 0 24 24" width="44" height="44" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
            <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
          </svg>
          <h2>Select an entry</h2>
          <p>Pick a credential from the list to reveal its username and password.</p>
        </div>

        <div v-else-if="isRevealing" class="vt-detail__empty">
          <span class="vt-spinner" aria-hidden="true"></span>
          <p>Decrypting…</p>
        </div>

        <div v-else-if="revealError" class="vt-detail__empty vt-detail__empty--error">
          <p>{{ revealError }}</p>
          <button type="button" class="vt-btn vt-btn--ghost" @click="retryReveal">Retry</button>
        </div>

        <article v-else-if="revealed" class="vt-detail__card">
          <button
            type="button"
            class="vt-detail__back"
            @click="clearSelection"
            aria-label="Back to list"
          >
            <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <polyline points="15 18 9 12 15 6"/>
            </svg>
            Back
          </button>

          <header class="vt-detail__head">
            <span
              class="vt-avatar vt-avatar--lg"
              :class="{ 'vt-avatar--logo': logoFor(revealed) }"
              :data-cat="revealed.category"
              aria-hidden="true"
            >
              <img
                v-if="logoFor(revealed)"
                :src="logoFor(revealed).src"
                :alt="revealed.name"
                @error="onLogoError(revealed, logoFor(revealed).source)"
              />
              <template v-else>{{ initialsFor(revealed.name) }}</template>
            </span>
            <div class="vt-detail__heading">
              <h2>
                {{ revealed.name }}
                <span v-if="revealed.pinned" class="vt-detail__pin" title="Pinned">★</span>
              </h2>
              <div class="vt-detail__meta">
                <span class="vt-pill vt-pill--blue">{{ categoryLabel(revealed.category) }}</span>
                <a
                  v-if="revealed.url"
                  :href="revealed.url"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="vt-detail__link"
                >Open site
                  <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
                    <polyline points="15 3 21 3 21 9"/>
                    <line x1="10" y1="14" x2="21" y2="3"/>
                  </svg>
                </a>
              </div>
            </div>
            <div class="vt-detail__head-actions">
              <button
                type="button"
                class="vt-icon-btn"
                title="Edit"
                @click="goEdit(revealed.id)"
              >
                <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                  <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                  <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                </svg>
              </button>
              <button
                type="button"
                class="vt-icon-btn vt-icon-btn--danger"
                title="Delete"
                @click="confirmDelete(revealed)"
              >
                <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                  <polyline points="3 6 5 6 21 6"/>
                  <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
                </svg>
              </button>
            </div>
          </header>

          <div v-if="copyToast" class="vt-toast">{{ copyToast }}</div>

          <div class="vt-fields">
            <div v-if="!fieldsList.length && !revealed.notes" class="vt-fields__empty">
              No credentials saved for this entry yet. Click <strong>Edit</strong> to add one.
            </div>

            <div
              v-for="(field, idx) in fieldsList"
              :key="idx"
              class="vt-field"
            >
              <div class="vt-field__label">{{ field.label }}</div>
              <div class="vt-field__value">
                <span :class="['vt-field__text', { 'vt-field__text--mono': field.is_secret }]">
                  {{ displayValue(field, idx) }}
                </span>
                <div class="vt-field__actions">
                  <button
                    v-if="field.is_secret"
                    type="button"
                    class="vt-icon-btn"
                    :title="revealedRows[idx] ? 'Hide' : 'Show'"
                    @click="toggleReveal(idx)"
                  >
                    <svg v-if="!revealedRows[idx]" viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                      <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7S1 12 1 12z"/>
                      <circle cx="12" cy="12" r="3"/>
                    </svg>
                    <svg v-else viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/>
                      <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/>
                      <line x1="1" y1="1" x2="23" y2="23"/>
                    </svg>
                  </button>
                  <button
                    type="button"
                    class="vt-icon-btn"
                    title="Copy"
                    @click="copyValue(field.value, field.label)"
                  >
                    <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                      <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/>
                      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
                    </svg>
                  </button>
                </div>
              </div>
            </div>

            <div v-if="revealed.notes" class="vt-notes">
              <div class="vt-notes__label">Notes</div>
              <div class="vt-notes__body">{{ revealed.notes }}</div>
            </div>
          </div>

          <footer class="vt-detail__foot">
            <span>Last modified {{ formatDate(revealed.updated_at) }}</span>
          </footer>
        </article>
      </section>
    </div>

    <AppConfirmDialog
      :visible="deleteDialogOpen"
      title="Delete vault entry?"
      :message="deleteMessage"
      confirm-label="Delete"
      variant="danger"
      :busy="isDeleting"
      @confirm="executeDelete"
      @cancel="cancelDelete"
    />
  </div>
</template>

<script>
import axios from 'axios'
import AppDropdown from '@/components/AppDropdown.vue'
import AppConfirmDialog from '@/components/AppConfirmDialog.vue'
import { resolveProviderLogo } from './providerLogos'

const CATEGORY_LABELS = {
  payment_gateway: 'Payment Gateway',
  email:           'Email',
  hosting:         'Hosting',
  domain:          'Domain / DNS',
  database:        'Database',
  cloud:           'Cloud',
  api:             'API / Integration',
  shipping:        'Shipping',
  social:          'Social',
  ads:             'Ads',
  analytics:       'Analytics',
  sms:             'SMS / OTP',
  storage:         'Storage',
  internal:        'Internal Tool',
  other:           'Other',
}

export default {
  name: 'Vault',
  components: { AppDropdown, AppConfirmDialog },
  data() {
    return {
      items: [],
      categories: [],
      isLoading: false,
      errorMessage: '',
      searchTerm: '',
      categoryFilter: null,

      selectedId: null,
      revealed: null,
      revealedRows: {},
      isRevealing: false,
      revealError: '',

      copyToast: '',
      copyToastTimer: null,

      // Records which logo sources failed for each entry, per source
      // ('curated', 'favicon'). A 404 on the curated SVG marks
      // 'curated' as broken so the next render tries the favicon; if
      // that 404s too, the avatar falls back to initials.
      logoFailures: {},

      // Pincode gate. `lockStatus` drives which UI block renders:
      //  - 'unknown' — initial state, before /vault/status returns
      //  - 'locked'  — show the pin entry overlay
      //  - 'unlocked'— show the master/detail UI
      lockStatus: 'unknown',
      pinConfigured: true,
      pinDigits: ['', '', '', '', '', ''],
      pinError: '',
      isUnlocking: false,

      // Bank-vault dial animation states:
      //  - 'idle'     — slow continuous rotation
      //  - 'typing'   — single tick step when a digit is entered
      //  - 'cracking' — fast spin while the unlock POST is in flight
      //  - 'unlocked' — final overshoot-and-settle clunk on success
      dialState: 'idle',
      _dialResetTimer: null,
      // True briefly between a successful unlock and the lockStatus
      // flip — keeps the lockscreen card rendered so its "opening"
      // animation can play before the master/detail UI swaps in.
      unlockAnimating: false,
      // Toggled on a wrong pin to shake the lockscreen card.
      cardShake: false,

      deleteDialogOpen: false,
      pendingDelete: null,
      isDeleting: false,
    }
  },
  computed: {
    categoryOptions() {
      const opts = (this.categories.length ? this.categories : Object.keys(CATEGORY_LABELS))
        .map((c) => ({ value: c, label: CATEGORY_LABELS[c] || c }))
      return [{ value: null, label: 'All categories' }, ...opts]
    },
    filteredEntries() {
      const term = (this.searchTerm || '').trim().toLowerCase()
      return this.items.filter((row) => {
        if (this.categoryFilter && row.category !== this.categoryFilter) {
          return false
        }
        if (!term) {
          return true
        }
        const haystack = `${row.name || ''} ${row.hint || ''} ${row.url || ''}`.toLowerCase()
        return haystack.includes(term)
      })
    },
    fieldsList() {
      if (!this.revealed || !Array.isArray(this.revealed.fields)) {
        return []
      }
      return this.revealed.fields
    },
    deleteMessage() {
      if (!this.pendingDelete) {
        return ''
      }
      return `“${this.pendingDelete.name}” will be permanently removed. The audit log keeps a record of the deletion.`
    },
  },
  methods: {
    categoryLabel(cat) {
      return CATEGORY_LABELS[cat] || cat
    },
    initialsFor(name) {
      if (!name) {
        return '?'
      }
      const parts = name.split(/\s+/).filter(Boolean).slice(0, 2)
      return parts.map((p) => p[0].toUpperCase()).join('')
    },
    logoFor(entry) {
      if (!entry) {
        return null
      }
      return resolveProviderLogo(entry, this.logoFailures[entry.id] || [])
    },
    onLogoError(entry, source) {
      const prev = this.logoFailures[entry.id] || []
      if (prev.includes(source)) {
        return
      }
      this.$set(this.logoFailures, entry.id, [...prev, source])
    },
    formatDate(value) {
      if (!value) {
        return '—'
      }
      const d = new Date(value)
      if (Number.isNaN(d.getTime())) {
        return value
      }
      return d.toLocaleString('en-IN', {
        day:    '2-digit',
        month:  'short',
        year:   'numeric',
        hour:   '2-digit',
        minute: '2-digit',
      })
    },
    displayValue(field, idx) {
      if (!field.value) {
        return '—'
      }
      if (field.is_secret && !this.revealedRows[idx]) {
        return '••••••••••••'
      }
      return field.value
    },
    toggleReveal(idx) {
      this.$set(this.revealedRows, idx, !this.revealedRows[idx])
    },
    selectEntry(entry) {
      if (entry.id === this.selectedId && this.revealed) {
        return
      }
      this.selectedId = entry.id
      this.revealed = null
      this.revealedRows = {}
      this.revealError = ''
      this.loadReveal(entry.id)
    },
    retryReveal() {
      if (this.selectedId) {
        this.loadReveal(this.selectedId)
      }
    },
    clearSelection() {
      this.selectedId = null
      this.revealed = null
      this.revealedRows = {}
      this.revealError = ''
    },
    goCreate() {
      this.$router.push({ path: '/vault/new' })
    },
    goEdit(id) {
      this.$router.push({ path: `/vault/${id}` })
    },
    copyValue(value, label) {
      if (!value) {
        return
      }
      const showToast = (msg) => {
        this.copyToast = msg
        if (this.copyToastTimer) {
          clearTimeout(this.copyToastTimer)
        }
        this.copyToastTimer = setTimeout(() => {
          this.copyToast = ''
        }, 1600)
      }
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(value)
          .then(() => showToast(`${label || 'Value'} copied`))
          .catch(() => showToast('Copy failed'))
      } else {
        showToast('Clipboard unavailable')
      }
    },
    confirmDelete(row) {
      this.pendingDelete = row
      this.deleteDialogOpen = true
    },
    cancelDelete() {
      this.deleteDialogOpen = false
      this.pendingDelete = null
    },
    executeDelete() {
      if (!this.pendingDelete) {
        return
      }
      const id = this.pendingDelete.id
      const self = this
      this.isDeleting = true
      axios.delete(`${this.$apiAdress}/api/vault/${id}`)
        .then(function () {
          self.items = self.items.filter((row) => row.id !== id)
          if (self.selectedId === id) {
            self.clearSelection()
          }
          self.cancelDelete()
        })
        .catch(function (error) {
          self.errorMessage = self.extractError(error, 'Could not delete entry.')
        })
        .finally(function () {
          self.isDeleting = false
        })
    },
    loadEntries() {
      const self = this
      this.isLoading = true
      this.errorMessage = ''
      axios.get(`${this.$apiAdress}/api/vault`)
        .then(function (response) {
          const payload = response.data || {}
          self.items = Array.isArray(payload.data) ? payload.data : []
          self.categories = Array.isArray(payload.categories) ? payload.categories : []
          if (self.selectedId !== null && !self.items.some((row) => row.id === self.selectedId)) {
            self.clearSelection()
          }
        })
        .catch(function (error) {
          if (self.handleLockedResponse(error)) {
            return
          }
          self.errorMessage = self.extractError(error, 'Could not load vault entries.')
          self.items = []
        })
        .finally(function () {
          self.isLoading = false
        })
    },
    loadReveal(id) {
      const self = this
      this.isRevealing = true
      this.revealError = ''
      axios.get(`${this.$apiAdress}/api/vault/${id}`)
        .then(function (response) {
          if (self.selectedId !== id) {
            return
          }
          self.revealed = response.data || null
        })
        .catch(function (error) {
          if (self.selectedId !== id) {
            return
          }
          if (self.handleLockedResponse(error)) {
            return
          }
          self.revealError = self.extractError(error, 'Could not reveal entry.')
        })
        .finally(function () {
          if (self.selectedId === id) {
            self.isRevealing = false
          }
        })
    },

    // ── Pincode gate ────────────────────────────────────────
    checkLockStatus() {
      const self = this
      axios.get(`${this.$apiAdress}/api/vault/status`)
        .then(function (response) {
          const data = response.data || {}
          self.pinConfigured = data.configured !== false
          self.lockStatus = data.unlocked ? 'unlocked' : 'locked'
          if (self.lockStatus === 'unlocked') {
            self.loadEntries()
          } else {
            self.$nextTick(() => self.focusFirstPinBox())
          }
        })
        .catch(function (error) {
          self.lockStatus = 'locked'
          self.errorMessage = self.extractError(error, 'Could not check vault status.')
        })
    },
    focusFirstPinBox() {
      const inputs = this.$refs.pinInputs
      if (Array.isArray(inputs) && inputs[0]) {
        inputs[0].focus()
      }
    },
    onPinInput(idx, event) {
      const raw = (event.target.value || '').replace(/\D/g, '')
      const digit = raw.slice(-1)
      this.$set(this.pinDigits, idx, digit)
      // Re-sync the DOM in case the user typed a non-digit — Vue won't
      // re-render an input with the same model value, so we set it
      // explicitly to clear the rejected character.
      event.target.value = digit
      this.pinError = ''
      if (digit) {
        this.tickDial()
      }
      if (digit && idx < 5) {
        const next = this.$refs.pinInputs && this.$refs.pinInputs[idx + 1]
        if (next) {
          next.focus()
          next.select()
        }
      } else if (digit && idx === 5) {
        this.submitPin()
      }
    },
    // Snaps the dial through a single tick step. Animations bound to
    // the 'typing' class auto-reset back to 'idle' after a short delay
    // so the rotor returns to its slow continuous spin.
    tickDial() {
      this.dialState = 'typing'
      if (this._dialResetTimer) {
        clearTimeout(this._dialResetTimer)
      }
      this._dialResetTimer = setTimeout(() => {
        if (this.dialState === 'typing') {
          this.dialState = 'idle'
        }
      }, 280)
    },
    onPinKeydown(idx, event) {
      if (event.key === 'Backspace' && !this.pinDigits[idx] && idx > 0) {
        const prev = this.$refs.pinInputs && this.$refs.pinInputs[idx - 1]
        if (prev) {
          prev.focus()
        }
      } else if (event.key === 'ArrowLeft' && idx > 0) {
        const prev = this.$refs.pinInputs && this.$refs.pinInputs[idx - 1]
        if (prev) {
          prev.focus()
        }
      } else if (event.key === 'ArrowRight' && idx < 5) {
        const next = this.$refs.pinInputs && this.$refs.pinInputs[idx + 1]
        if (next) {
          next.focus()
        }
      }
    },
    onPinPaste(event) {
      const text = (event.clipboardData && event.clipboardData.getData('text')) || ''
      const digits = text.replace(/\D/g, '').slice(0, 6).split('')
      if (!digits.length) {
        return
      }
      for (let i = 0; i < 6; i += 1) {
        this.$set(this.pinDigits, i, digits[i] || '')
      }
      const inputs = this.$refs.pinInputs || []
      inputs.forEach((el, i) => { if (el) { el.value = this.pinDigits[i] || '' } })
      this.pinError = ''
      if (digits.length === 6) {
        this.submitPin()
      } else {
        const next = inputs[digits.length]
        if (next) {
          next.focus()
        }
      }
    },
    submitPin() {
      const pin = this.pinDigits.join('')
      if (pin.length !== 6 || this.isUnlocking) {
        return
      }
      const self = this
      this.isUnlocking = true
      this.pinError = ''
      this.dialState = 'cracking'
      axios.post(`${this.$apiAdress}/api/vault/unlock`, { pin })
        .then(function () {
          // Sequence the bank-locker open:
          //   1. dial settles with an overshoot ('unlocked' clunk)
          //   2. card scales up and fades ('opening')
          //   3. lockStatus flips to 'unlocked' and the layout renders
          self.dialState = 'unlocked'
          self.unlockAnimating = true
          setTimeout(() => {
            self.lockStatus = 'unlocked'
            self.unlockAnimating = false
            self.dialState = 'idle'
            self.pinDigits = ['', '', '', '', '', '']
            self.loadEntries()
          }, 750)
        })
        .catch(function (error) {
          self.pinError = self.extractError(error, 'Could not verify pincode.')
          self.dialState = 'idle'
          self.cardShake = true
          setTimeout(() => { self.cardShake = false }, 600)
          self.pinDigits = ['', '', '', '', '', '']
          const inputs = self.$refs.pinInputs || []
          inputs.forEach((el) => { if (el) { el.value = '' } })
          self.$nextTick(() => self.focusFirstPinBox())
        })
        .finally(function () {
          self.isUnlocking = false
        })
    },
    lockVault() {
      const self = this
      axios.post(`${this.$apiAdress}/api/vault/lock`)
        .finally(function () {
          self.lockStatus = 'locked'
          self.dialState = 'idle'
          self.items = []
          self.clearSelection()
          self.$nextTick(() => self.focusFirstPinBox())
        })
    },
    handleLockedResponse(error) {
      // A 423 from any vault endpoint means the TTL expired (or the
      // user was locked from another tab). Flip back to the prompt
      // and discard any half-loaded data so secrets don't linger.
      if (error && error.response && error.response.status === 423) {
        this.lockStatus = 'locked'
        this.items = []
        this.clearSelection()
        this.$nextTick(() => this.focusFirstPinBox())
        return true
      }
      return false
    },

    extractError(error, fallback) {
      const data = error && error.response && error.response.data
      if (data && data.message) {
        return data.message
      }
      return fallback
    },
  },
  mounted() {
    this.checkLockStatus()
  },
  // Vault.vue is kept-alive by TheContainer so search/filter state
  // survives navigation. We must still re-fetch on every return so
  // the list reflects any create/edit/delete that happened on the
  // form page — and re-check the lock status in case the TTL expired
  // while the user was elsewhere in the app.
  activated() {
    this.checkLockStatus()
    if (this.lockStatus === 'unlocked' && this.selectedId !== null) {
      this.loadReveal(this.selectedId)
    }
  },
  beforeDestroy() {
    if (this.copyToastTimer) {
      clearTimeout(this.copyToastTimer)
    }
    if (this._dialResetTimer) {
      clearTimeout(this._dialResetTimer)
    }
  },
}
</script>

<style scoped>
.vt-page {
  padding-left: 1rem;
  padding-right: 1rem;
}

.vt-toolbar {
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  align-items: flex-start;
  margin-bottom: 1rem;
}

.vt-toolbar__title {
  display: flex;
  gap: 0.85rem;
  align-items: stretch;
}

.vt-toolbar__accent {
  width: 4px;
  border-radius: 2px;
  background: linear-gradient(180deg, #0e5b46 0%, #16a07a 100%);
}

.vt-toolbar__title h1 {
  font-size: 1.15rem;
  font-weight: 700;
  color: #1f2a44;
  margin: 0;
  letter-spacing: -0.01em;
}

.vt-toolbar__title p {
  font-size: 0.78rem;
  color: #6b7280;
  margin: 0.2rem 0 0;
  max-width: 640px;
}

.vt-toolbar__actions {
  display: inline-flex;
  gap: 0.55rem;
  flex-wrap: wrap;
}

.vt-alert {
  border-radius: 10px;
  font-size: 0.85rem;
}

.vt-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 36px;
  min-width: 96px;
  padding: 0 1rem;
  border-radius: 8px;
  font-family: inherit;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  border: 0;
  transition: transform 0.15s ease, box-shadow 0.15s ease, filter 0.15s ease, background 0.15s ease, color 0.15s ease, border-color 0.15s ease;
}

.vt-btn:focus { outline: none; }
.vt-btn::-moz-focus-inner { border: 0; }
.vt-btn:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.22);
}

.vt-btn:disabled {
  cursor: not-allowed;
  opacity: 0.6;
}

.vt-btn--primary {
  background: linear-gradient(135deg, #0e5b46 0%, #16a07a 100%);
  color: #ffffff;
  box-shadow: 0 2px 6px rgba(14, 91, 70, 0.18);
}

.vt-btn--primary:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 6px 14px rgba(14, 91, 70, 0.28);
  filter: brightness(1.05);
}

.vt-btn--ghost {
  background: #ffffff;
  border: 1px solid #e5e9f2;
  color: #1f2a44;
}

.vt-btn--ghost:hover:not(:disabled) {
  background: #f8fafc;
  border-color: #d8dde6;
}

.vt-btn--danger {
  color: #b91c1c;
}

.vt-btn--danger:hover:not(:disabled) {
  border-color: #ef4444;
  background: #fef2f2;
}

.vt-btn--danger:focus-visible {
  box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.30);
}

/* ── Lockscreen ─────────────────────────────────────────── */
.vt-lockscreen {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 480px;
  padding: 1.5rem;
}

.vt-lockscreen > p {
  color: #6b7280;
  font-size: 0.85rem;
  margin: 0.8rem 0 0;
}

.vt-lockscreen__card {
  width: 100%;
  max-width: 440px;
  background: #ffffff;
  border: 1px solid #e5e9f2;
  border-radius: 14px;
  box-shadow: 0 16px 40px rgba(15, 23, 42, 0.08);
  padding: 1.8rem 1.6rem 1.6rem;
  text-align: center;
  transform-origin: center;
}

.vt-lockscreen__card--shake {
  animation: vt-shake 0.55s cubic-bezier(0.36, 0.07, 0.19, 0.97);
}

.vt-lockscreen__card--shake .vt-lockscreen__dial {
  background: radial-gradient(circle at 30% 30%, #d34141 0%, #b91c1c 55%, #7f1d1d 100%);
  box-shadow:
    0 8px 22px rgba(185, 28, 28, 0.32),
    inset 0 0 0 2px rgba(255, 255, 255, 0.10),
    inset 0 -4px 10px rgba(0, 0, 0, 0.25);
  transition: background 0.2s ease, box-shadow 0.2s ease;
}

.vt-lockscreen__card--opening {
  animation: vt-open 0.75s cubic-bezier(0.4, 0, 0.2, 1) forwards;
}

@keyframes vt-shake {
  10%, 90%        { transform: translateX(-2px); }
  20%, 80%        { transform: translateX(4px); }
  30%, 50%, 70%   { transform: translateX(-8px); }
  40%, 60%        { transform: translateX(8px); }
}

@keyframes vt-open {
  0%   { transform: scale(1);    opacity: 1; filter: blur(0); }
  35%  { transform: scale(1.04); opacity: 1; filter: blur(0); }
  100% { transform: scale(1.14); opacity: 0; filter: blur(3px); }
}

/* ── Vault dial ─────────────────────────────────────────── */
.vt-lockscreen__dial {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 76px;
  height: 76px;
  border-radius: 50%;
  background: radial-gradient(circle at 30% 30%, #1a7a5e 0%, #0e5b46 55%, #0a3f31 100%);
  box-shadow:
    0 8px 22px rgba(14, 91, 70, 0.28),
    inset 0 0 0 2px rgba(255, 255, 255, 0.08),
    inset 0 -4px 10px rgba(0, 0, 0, 0.25),
    inset 0 4px 10px rgba(255, 255, 255, 0.10);
  margin-bottom: 1rem;
}

.vt-lockscreen__dial svg {
  display: block;
  width: 56px;
  height: 56px;
}

.vt-dial__rotor {
  transform-origin: 30px 30px;
  /* Vue toggles the parent class — these animations drive rotation. */
  animation-fill-mode: forwards;
}

.vt-lockscreen__dial--idle .vt-dial__rotor {
  animation: vt-dial-idle 14s linear infinite;
}

.vt-lockscreen__dial--typing .vt-dial__rotor {
  animation: vt-dial-tick 0.28s cubic-bezier(0.34, 1.6, 0.64, 1);
}

.vt-lockscreen__dial--cracking .vt-dial__rotor {
  animation: vt-dial-crack 0.55s linear infinite;
}

.vt-lockscreen__dial--unlocked .vt-dial__rotor {
  animation: vt-dial-clunk 0.75s cubic-bezier(0.34, 1.56, 0.64, 1) forwards;
}

@keyframes vt-dial-idle {
  to { transform: rotate(360deg); }
}

@keyframes vt-dial-tick {
  0%   { transform: rotate(0); }
  55%  { transform: rotate(28deg); }
  100% { transform: rotate(0); }
}

@keyframes vt-dial-crack {
  to { transform: rotate(720deg); }
}

@keyframes vt-dial-clunk {
  0%   { transform: rotate(0); }
  60%  { transform: rotate(420deg); }
  100% { transform: rotate(360deg); }
}

.vt-lockscreen__card h2 {
  font-size: 1.1rem;
  font-weight: 700;
  color: #1f2a44;
  margin: 0 0 0.35rem;
}

.vt-lockscreen__card p {
  font-size: 0.82rem;
  color: #6b7280;
  margin: 0 0 1.1rem;
}

.vt-lockscreen__form {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.9rem;
}

.vt-pin {
  display: inline-flex;
  gap: 0.45rem;
}

.vt-pin__box {
  width: 44px;
  height: 52px;
  border-radius: 10px;
  border: 1px solid #d8dde6;
  background: #fbfcfe;
  font-size: 1.25rem;
  font-weight: 700;
  text-align: center;
  color: #1f2a44;
  outline: none;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  transition: border-color 0.15s ease, box-shadow 0.15s ease, background 0.15s ease;
  caret-color: #16a07a;
}

.vt-pin__box:focus {
  border-color: #16a07a;
  background: #ffffff;
  box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.18);
}

.vt-pin__box--filled {
  border-color: #16a07a;
  background: #ffffff;
  color: #0e5b46;
}

.vt-pin__box:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.vt-lockscreen__error {
  font-size: 0.82rem;
  color: #b91c1c;
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 8px;
  padding: 0.5rem 0.8rem;
  font-weight: 600;
}

.vt-lockscreen__submit {
  min-width: 180px;
}

.vt-lockscreen__alert {
  font-size: 0.82rem;
  color: #92400e;
  background: #fef3c7;
  border: 1px solid #fcd34d;
  border-radius: 8px;
  padding: 0.7rem 0.85rem;
  text-align: left;
  line-height: 1.5;
}

.vt-lockscreen__alert code {
  background: rgba(146, 64, 14, 0.12);
  border-radius: 4px;
  padding: 0.05rem 0.3rem;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  font-size: 0.78rem;
}

/* ── Master / detail layout ─────────────────────────────── */
.vt-layout {
  display: grid;
  grid-template-columns: minmax(280px, 360px) 1fr;
  gap: 1rem;
  align-items: stretch;
  min-height: 540px;
}

.vt-rail {
  background: #ffffff;
  border: 1px solid #e5e9f2;
  border-radius: 12px;
  box-shadow: 0 2px 6px rgba(15, 23, 42, 0.04);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  min-height: 540px;
}

.vt-rail__head {
  padding: 0.75rem 0.75rem 0.6rem;
  border-bottom: 1px solid #eef1f7;
  background: #fbfcfe;
  display: flex;
  flex-direction: column;
  gap: 0.55rem;
}

.vt-rail__search {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  height: 36px;
  padding: 0 0.7rem;
  border-radius: 8px;
  border: 1px solid #d8dde6;
  background: #ffffff;
  color: #6b7280;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.vt-rail__search:focus-within {
  border-color: #16a07a;
  box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.18);
  color: #0e5b46;
}

.vt-rail__search-input {
  flex: 1 1 auto;
  border: 0;
  background: transparent;
  font-size: 0.85rem;
  color: #1f2a44;
  outline: none;
  padding: 0;
}

.vt-rail__search-input::placeholder {
  color: #94a3b8;
}

.vt-rail__count {
  padding: 0.45rem 0.85rem;
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #6b7280;
  background: #ffffff;
  border-bottom: 1px solid #eef1f7;
}

.vt-rail__list {
  flex: 1 1 auto;
  overflow-y: auto;
  padding: 0.35rem;
}

.vt-rail__placeholder {
  padding: 1.2rem 0.85rem;
  font-size: 0.82rem;
  color: #6b7280;
  text-align: center;
}

.vt-rail__item {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 0.65rem;
  padding: 0.55rem 0.7rem;
  background: transparent;
  border: 1px solid transparent;
  border-radius: 10px;
  cursor: pointer;
  text-align: left;
  font-family: inherit;
  color: #1f2a44;
  transition: background 0.12s ease, border-color 0.12s ease, color 0.12s ease;
}

.vt-rail__item:hover {
  background: #f8fafc;
}

.vt-rail__item.is-active {
  background: #f0faf5;
  border-color: #cdeede;
}

.vt-rail__item:focus { outline: none; }
.vt-rail__item::-moz-focus-inner { border: 0; }
.vt-rail__item:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.22);
}

.vt-rail__item-text {
  display: flex;
  flex-direction: column;
  min-width: 0;
  flex: 1 1 auto;
}

.vt-rail__item-name {
  font-size: 0.88rem;
  font-weight: 700;
  color: #1f2a44;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
}

.vt-rail__item.is-active .vt-rail__item-name {
  color: #0e5b46;
}

.vt-rail__pin {
  color: #f59e0b;
  font-size: 0.78rem;
}

.vt-rail__item-id {
  font-size: 0.75rem;
  color: #6b7280;
  margin-top: 0.1rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* ── Avatar (initials chip) ─────────────────────────────── */
.vt-avatar {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background: linear-gradient(135deg, #0e5b46 0%, #16a07a 100%);
  color: #ffffff;
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.02em;
  box-shadow: 0 1px 3px rgba(14, 91, 70, 0.18);
}

.vt-avatar[data-cat="email"]           { background: linear-gradient(135deg, #1e40af 0%, #3b82f6 100%); }
.vt-avatar[data-cat="payment_gateway"] { background: linear-gradient(135deg, #6d28d9 0%, #a855f7 100%); }
.vt-avatar[data-cat="domain"]          { background: linear-gradient(135deg, #b45309 0%, #f59e0b 100%); }
.vt-avatar[data-cat="hosting"]         { background: linear-gradient(135deg, #1f2937 0%, #4b5563 100%); }
.vt-avatar[data-cat="database"]        { background: linear-gradient(135deg, #0f766e 0%, #14b8a6 100%); }
.vt-avatar[data-cat="cloud"]           { background: linear-gradient(135deg, #1d4ed8 0%, #60a5fa 100%); }
.vt-avatar[data-cat="api"]             { background: linear-gradient(135deg, #b91c1c 0%, #f87171 100%); }
.vt-avatar[data-cat="social"]          { background: linear-gradient(135deg, #be185d 0%, #f472b6 100%); }
.vt-avatar[data-cat="ads"]             { background: linear-gradient(135deg, #9a3412 0%, #fb923c 100%); }
.vt-avatar[data-cat="sms"]             { background: linear-gradient(135deg, #047857 0%, #34d399 100%); }

.vt-avatar--lg {
  width: 52px;
  height: 52px;
  border-radius: 12px;
  font-size: 1.05rem;
}

/* Logo variant: drop the brand gradient and host the provider's own
   SVG on a neutral white tile so the brand colour reads correctly.
   Applies regardless of the category data-attribute. */
.vt-avatar--logo,
.vt-avatar--logo[data-cat] {
  background: #ffffff;
  border: 1px solid #e5e9f2;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.06);
  padding: 6px;
}

.vt-avatar--logo.vt-avatar--lg,
.vt-avatar--logo.vt-avatar--lg[data-cat] {
  padding: 9px;
}

.vt-avatar--logo img {
  width: 100%;
  height: 100%;
  object-fit: contain;
  display: block;
}

/* ── Detail pane ────────────────────────────────────────── */
.vt-detail {
  background: #ffffff;
  border: 1px solid #e5e9f2;
  border-radius: 12px;
  box-shadow: 0 2px 6px rgba(15, 23, 42, 0.04);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  min-height: 540px;
}

.vt-detail__empty {
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 2rem 1.5rem;
  color: #6b7280;
  gap: 0.5rem;
}

.vt-detail__empty svg {
  color: #c8d1de;
}

.vt-detail__empty h2 {
  font-size: 1rem;
  font-weight: 700;
  color: #1f2a44;
  margin: 0;
}

.vt-detail__empty p {
  font-size: 0.85rem;
  margin: 0;
  max-width: 320px;
}

.vt-detail__empty--error p {
  color: #b91c1c;
}

.vt-detail__card {
  position: relative;
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-height: 0;
}

.vt-detail__back {
  display: none;
}

.vt-detail__head {
  display: flex;
  align-items: center;
  gap: 0.85rem;
  padding: 1rem 1.1rem;
  border-bottom: 1px solid #eef1f7;
  background: #fbfcfe;
}

.vt-detail__heading {
  flex: 1 1 auto;
  min-width: 0;
}

.vt-detail__heading h2 {
  font-size: 1.1rem;
  font-weight: 700;
  color: #1f2a44;
  margin: 0 0 0.25rem;
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  letter-spacing: -0.01em;
}

.vt-detail__pin {
  color: #f59e0b;
  font-size: 0.95rem;
}

.vt-detail__meta {
  display: inline-flex;
  align-items: center;
  gap: 0.55rem;
  flex-wrap: wrap;
}

.vt-pill {
  display: inline-flex;
  align-items: center;
  padding: 0.12rem 0.55rem;
  border-radius: 999px;
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.02em;
}

.vt-pill--blue { background: #dbeafe; color: #1e40af; }

.vt-detail__link {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  font-size: 0.78rem;
  font-weight: 600;
  color: #0e5b46;
}

.vt-detail__link:hover {
  color: #16a07a;
  text-decoration: underline;
}

.vt-detail__head-actions {
  display: inline-flex;
  gap: 0.4rem;
}

.vt-icon-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  border: 1px solid #e5e9f2;
  background: #ffffff;
  color: #4f5560;
  cursor: pointer;
  transition: border-color 0.15s ease, color 0.15s ease, background 0.15s ease, box-shadow 0.15s ease;
}

.vt-icon-btn:hover {
  border-color: #16a07a;
  color: #0e5b46;
  background: #f5fbf8;
}

.vt-icon-btn:focus { outline: none; }
.vt-icon-btn::-moz-focus-inner { border: 0; }
.vt-icon-btn:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.22);
}

.vt-icon-btn--danger:hover {
  border-color: #ef4444;
  color: #b91c1c;
  background: #fef2f2;
}

.vt-icon-btn--danger:focus-visible {
  box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.30);
}

.vt-toast {
  position: absolute;
  top: 0.65rem;
  right: 0.85rem;
  background: #0e5b46;
  color: #ffffff;
  padding: 0.35rem 0.7rem;
  border-radius: 6px;
  font-size: 0.76rem;
  font-weight: 600;
  box-shadow: 0 4px 12px rgba(14, 91, 70, 0.28);
  z-index: 5;
}

.vt-fields {
  flex: 1 1 auto;
  overflow-y: auto;
  padding: 0.85rem 1.1rem 1.1rem;
  display: flex;
  flex-direction: column;
  gap: 0.7rem;
}

.vt-fields__empty {
  font-size: 0.85rem;
  color: #6b7280;
  background: #fbfcfe;
  border: 1px dashed #d8dde6;
  border-radius: 8px;
  padding: 0.85rem;
  text-align: center;
}

.vt-field {
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
  padding-bottom: 0.7rem;
  border-bottom: 1px solid #eef1f7;
}

.vt-field:last-of-type {
  border-bottom: 0;
}

.vt-field__label {
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #6b7280;
}

.vt-field__value {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.6rem;
}

.vt-field__text {
  flex: 1 1 auto;
  min-width: 0;
  font-size: 0.95rem;
  font-weight: 600;
  color: #1f2a44;
  word-break: break-all;
}

.vt-field__text--mono {
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  letter-spacing: 0.02em;
  color: #16a07a;
}

.vt-field__actions {
  display: inline-flex;
  gap: 0.35rem;
  flex: 0 0 auto;
}

.vt-notes {
  margin-top: 0.55rem;
  padding-top: 0.85rem;
  border-top: 1px solid #eef1f7;
}

.vt-notes__label {
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #6b7280;
  margin-bottom: 0.35rem;
}

.vt-notes__body {
  font-size: 0.85rem;
  color: #1f2a44;
  white-space: pre-wrap;
  background: #fbfcfe;
  border: 1px solid #eef1f7;
  border-radius: 8px;
  padding: 0.7rem 0.85rem;
  line-height: 1.5;
}

.vt-detail__foot {
  padding: 0.6rem 1.1rem;
  border-top: 1px solid #eef1f7;
  background: #fbfcfe;
  font-size: 0.75rem;
  color: #6b7280;
}

.vt-spinner {
  width: 22px;
  height: 22px;
  border-radius: 50%;
  border: 2px solid #e5e9f2;
  border-top-color: #16a07a;
  animation: vt-spin 0.65s linear infinite;
}

@keyframes vt-spin {
  to { transform: rotate(360deg); }
}

/* ── Responsive ─────────────────────────────────────────── */
/* Tablets & landscape phones — collapse master/detail, stack the
   toolbar, drop the fixed min-heights so the page can shrink to fit. */
@media (max-width: 820px) {
  .vt-toolbar {
    flex-direction: column;
    align-items: stretch;
    gap: 0.75rem;
  }

  .vt-toolbar__actions {
    justify-content: flex-start;
  }

  .vt-layout {
    grid-template-columns: 1fr;
    gap: 0.75rem;
    min-height: 0;
  }

  .vt-rail {
    min-height: 320px;
  }

  .vt-detail {
    min-height: 420px;
  }

  .vt-rail--hidden-mobile {
    display: none;
  }

  .vt-detail--hidden-mobile {
    display: none;
  }

  .vt-detail__head {
    padding: 0.85rem 0.95rem;
    gap: 0.7rem;
  }

  .vt-avatar--lg {
    width: 44px;
    height: 44px;
    font-size: 0.92rem;
    border-radius: 10px;
  }

  .vt-detail__heading h2 {
    font-size: 1rem;
  }

  .vt-detail__back {
    display: inline-flex;
    align-items: center;
    gap: 0.3rem;
    margin: 0.75rem 0 0 0.85rem;
    padding: 0.3rem 0.55rem;
    font-size: 0.8rem;
    font-weight: 600;
    color: #0e5b46;
    background: transparent;
    border: 0;
    cursor: pointer;
    align-self: flex-start;
  }

  .vt-detail__back:focus { outline: none; }
  .vt-detail__back:focus-visible {
    outline: none;
    box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.22);
    border-radius: 6px;
  }
}

/* Phone-size — tighter paddings, smaller pin boxes, hidden subtitle,
   and a more compact lockscreen so the dial doesn't dominate. */
@media (max-width: 540px) {
  .vt-page {
    padding-left: 0.5rem;
    padding-right: 0.5rem;
  }

  .vt-toolbar__title h1 {
    font-size: 1.02rem;
  }

  /* Subtitle reads as filler on a phone — the title is enough. */
  .vt-toolbar__title p {
    display: none;
  }

  .vt-toolbar__actions {
    gap: 0.4rem;
    width: 100%;
  }

  .vt-btn {
    min-width: 0;
    padding: 0 0.75rem;
    font-size: 0.8rem;
    flex: 1 1 auto;
  }

  .vt-lockscreen {
    min-height: 0;
    padding: 0.85rem 0.4rem;
  }

  .vt-lockscreen__card {
    padding: 1.3rem 1.05rem 1.15rem;
    border-radius: 12px;
  }

  .vt-lockscreen__dial {
    width: 64px;
    height: 64px;
    margin-bottom: 0.75rem;
  }

  .vt-lockscreen__dial svg {
    width: 46px;
    height: 46px;
  }

  .vt-lockscreen__card h2 {
    font-size: 1rem;
  }

  .vt-lockscreen__card p {
    font-size: 0.78rem;
  }

  .vt-pin {
    gap: 0.3rem;
  }

  .vt-pin__box {
    width: 38px;
    height: 46px;
    font-size: 1.08rem;
    border-radius: 8px;
  }

  .vt-lockscreen__submit {
    width: 100%;
    min-width: 0;
  }

  /* Rail compaction: smaller search, tighter list rows so more
     entries fit on a phone screen. */
  .vt-rail__head {
    padding: 0.55rem 0.55rem 0.5rem;
    gap: 0.4rem;
  }

  .vt-rail__count {
    padding: 0.35rem 0.7rem;
  }

  .vt-rail__item {
    padding: 0.5rem 0.55rem;
    gap: 0.55rem;
  }

  .vt-avatar {
    width: 32px;
    height: 32px;
    border-radius: 8px;
    font-size: 0.72rem;
  }

  .vt-detail__head {
    flex-wrap: wrap;
    padding: 0.75rem 0.85rem;
  }

  .vt-detail__heading {
    /* Force the heading + meta block onto its own row when the
       avatar + actions can't share the line. */
    flex: 1 1 100%;
    order: 3;
    min-width: 0;
  }

  .vt-detail__head-actions {
    margin-left: auto;
  }

  .vt-fields {
    padding: 0.7rem 0.85rem 0.95rem;
    gap: 0.6rem;
  }

  .vt-field__text {
    font-size: 0.88rem;
  }

  .vt-toast {
    top: 0.5rem;
    right: 0.5rem;
    font-size: 0.72rem;
  }
}
</style>
